import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';

const MIAContext = createContext();

// Função para obter userId do token JWT
const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id || payload.userId || payload.sub;
  } catch {
    return null;
  }
};

// Chaves para localStorage por usuário
const getMIAEnabledKey = (userId) => `mensure_ia_enabled_${userId || 'guest'}`;
const getMIAActivatedKey = (userId) => `mensure_ia_activated_${userId || 'guest'}`;

/**
 * Lista de features controladas pelo MIA
 * Futuramente cada feature poderá estar em pacotes diferentes
 */
export const MIA_FEATURES = {
  SENTIMENT_AI: 'sentiment_ai',           // Análise de sentimento com IA
  SENTIMENT_PROPAGATION: 'sentiment_propagation', // Propagação de sentimento para similares
  AUTO_TAGS: 'auto_tags',                 // Tags automáticas por briefing
  SIMILARITY_DETECTION: 'similarity_detection', // Detecção de artigos similares
  SMART_SCRAPING: 'smart_scraping',       // Scraping inteligente
  CONTENT_SUMMARY: 'content_summary',     // Resumo automático de conteúdo
  KPIA_ANALYSIS: 'kpia_analysis',         // Valoração Multimodal KPIA
};

/**
 * Hook para usar o contexto MIA
 */
export const useMIA = () => {
  const context = useContext(MIAContext);
  if (!context) {
    throw new Error('useMIA must be used within a MIAProvider');
  }
  return context;
};

/**
 * Provider para o contexto MIA
 * Gerencia o estado global de ativação das funcionalidades de IA
 * Requer token de ativação fornecido pelo time Mensure
 */
export const MIAProvider = ({ children }) => {
  // ID do usuário atual
  const [currentUserId, setCurrentUserId] = useState(() => getUserIdFromToken());

  // Estado de ativação (requer token válido) - inicia false, backend decide
  const [activated, setActivated] = useState(false);

  // Estado enabled (usuário pode pausar temporariamente mesmo ativado)
  const [enabled, setEnabled] = useState(false);

  // Features ativadas pelo token
  const [features, setFeatures] = useState({
    sentimentAI: true,
    sentimentPropagation: true,
    autoTags: true,
    similarityDetection: true,
    smartScraping: true,
    contentSummary: true,
    kpiaAnalysis: false
  });

  // Data de expiração
  const [expiresAt, setExpiresAt] = useState(null);

  // Loading state
  const [loading, setLoading] = useState(false);

  // Verifica status no backend ao carregar e quando userId mudar
  useEffect(() => {
    const userId = getUserIdFromToken();
    setCurrentUserId(userId);

    const checkStatus = async () => {
      if (!userId) {
        // Sem usuário logado, reseta tudo
        setActivated(false);
        setEnabled(false);
        return;
      }

      try {
        const response = await api.get('/mia/status');
        const activatedKey = getMIAActivatedKey(userId);
        const enabledKey = getMIAEnabledKey(userId);

        if (response.data.activated) {
          setActivated(true);
          setFeatures(response.data.features || features);
          setExpiresAt(response.data.expiresAt);
          localStorage.setItem(activatedKey, JSON.stringify(true));

          // Carrega preferência de enabled do usuário
          const savedEnabled = localStorage.getItem(enabledKey);
          if (savedEnabled === null) {
            setEnabled(true);
            localStorage.setItem(enabledKey, JSON.stringify(true));
          } else {
            setEnabled(JSON.parse(savedEnabled));
          }
        } else {
          setActivated(false);
          setEnabled(false);
          localStorage.setItem(activatedKey, JSON.stringify(false));
          localStorage.setItem(enabledKey, JSON.stringify(false));
        }
      } catch (error) {
        // Se falhar, assume não ativado
        console.log('MIA status check failed');
        setActivated(false);
        setEnabled(false);
      }
    };

    // Só verifica se estiver autenticado
    const token = localStorage.getItem('token');
    if (token) {
      checkStatus();
    } else {
      setActivated(false);
      setEnabled(false);
    }

    // Listener para mudanças de storage (login/logout em outra aba)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        const newUserId = getUserIdFromToken();
        if (newUserId !== currentUserId) {
          setCurrentUserId(newUserId);
          if (newUserId) {
            checkStatus();
          } else {
            setActivated(false);
            setEnabled(false);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUserId]);

  /**
   * Ativa o MIA com um token
   * @param {string} token - Token de ativação
   * @returns {Object} - { success: boolean, error?: string }
   */
  const activate = useCallback(async (token) => {
    setLoading(true);
    try {
      const response = await api.post('/mia/activate', { token });

      if (response.data.success) {
        const userId = getUserIdFromToken();
        setActivated(true);
        setEnabled(true);
        setFeatures(response.data.features || features);
        setExpiresAt(response.data.expiresAt);

        localStorage.setItem(getMIAActivatedKey(userId), JSON.stringify(true));
        localStorage.setItem(getMIAEnabledKey(userId), JSON.stringify(true));

        return { success: true };
      }

      return { success: false, error: response.data.error || 'Erro desconhecido' };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao ativar token';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [features]);

  /**
   * Desativa o MIA (remove token)
   */
  const deactivate = useCallback(() => {
    const userId = getUserIdFromToken();
    setActivated(false);
    setEnabled(false);
    setFeatures({});
    setExpiresAt(null);
    localStorage.setItem(getMIAActivatedKey(userId), JSON.stringify(false));
    localStorage.setItem(getMIAEnabledKey(userId), JSON.stringify(false));
  }, []);

  /**
   * Toggle enabled (pausa temporária)
   */
  const toggle = useCallback(() => {
    if (!activated) return; // Só pode toggle se estiver ativado

    const userId = getUserIdFromToken();
    setEnabled(prev => {
      const newValue = !prev;
      try {
        localStorage.setItem(getMIAEnabledKey(userId), JSON.stringify(newValue));
      } catch (e) {
        console.error('Erro ao salvar preferência MIA:', e);
      }
      return newValue;
    });
  }, [activated]);

  /**
   * Ativar enabled
   */
  const enable = useCallback(() => {
    if (!activated) return;
    const userId = getUserIdFromToken();
    setEnabled(true);
    try {
      localStorage.setItem(getMIAEnabledKey(userId), JSON.stringify(true));
    } catch (e) {
      console.error('Erro ao salvar preferência MIA:', e);
    }
  }, [activated]);

  /**
   * Desativar enabled
   */
  const disable = useCallback(() => {
    const userId = getUserIdFromToken();
    setEnabled(false);
    try {
      localStorage.setItem(getMIAEnabledKey(userId), JSON.stringify(false));
    } catch (e) {
      console.error('Erro ao salvar preferência MIA:', e);
    }
  }, []);

  /**
   * Verifica se uma feature específica está disponível
   */
  const isFeatureEnabled = useCallback((feature) => {
    if (!activated || !enabled) return false;

    // Mapeia MIA_FEATURES para features do token
    const featureMap = {
      [MIA_FEATURES.SENTIMENT_AI]: 'sentimentAI',
      [MIA_FEATURES.SENTIMENT_PROPAGATION]: 'sentimentPropagation',
      [MIA_FEATURES.AUTO_TAGS]: 'autoTags',
      [MIA_FEATURES.SIMILARITY_DETECTION]: 'similarityDetection',
      [MIA_FEATURES.SMART_SCRAPING]: 'smartScraping',
      [MIA_FEATURES.CONTENT_SUMMARY]: 'contentSummary',
      [MIA_FEATURES.KPIA_ANALYSIS]: 'kpiaAnalysis',
    };

    const featureKey = featureMap[feature];
    return featureKey ? features[featureKey] !== false : true;
  }, [activated, enabled, features]);

  /**
   * Verifica se o sentimento com IA está ativado
   */
  const isSentimentAIEnabled = useCallback(() => {
    return isFeatureEnabled(MIA_FEATURES.SENTIMENT_AI);
  }, [isFeatureEnabled]);

  /**
   * Verifica se propagação de sentimento está ativada
   */
  const isSentimentPropagationEnabled = useCallback(() => {
    return isFeatureEnabled(MIA_FEATURES.SENTIMENT_PROPAGATION);
  }, [isFeatureEnabled]);

  /**
   * Verifica se tags automáticas estão ativadas
   */
  const isAutoTagsEnabled = useCallback(() => {
    return isFeatureEnabled(MIA_FEATURES.AUTO_TAGS);
  }, [isFeatureEnabled]);

  /**
   * Verifica se detecção de similaridade está ativada
   */
  const isSimilarityEnabled = useCallback(() => {
    return isFeatureEnabled(MIA_FEATURES.SIMILARITY_DETECTION);
  }, [isFeatureEnabled]);

  /**
   * Verifica se a Valoração Multimodal KPIA está ativada
   */
  const isKpiaEnabled = useCallback(() => {
    return isFeatureEnabled(MIA_FEATURES.KPIA_ANALYSIS);
  }, [isFeatureEnabled]);

  const value = {
    // Estados
    activated,      // Token válido ativado
    enabled,        // Usuário habilitou (pode pausar)
    features,       // Features disponíveis
    expiresAt,      // Data de expiração
    loading,        // Carregando ativação

    // Ações
    activate,       // Ativa com token
    deactivate,     // Remove ativação
    toggle,         // Toggle enabled
    enable,         // Habilita
    disable,        // Desabilita

    // Verificações de features
    isFeatureEnabled,
    isSentimentAIEnabled,
    isSentimentPropagationEnabled,
    isAutoTagsEnabled,
    isSimilarityEnabled,
    isKpiaEnabled,

    // Constantes
    MIA_FEATURES
  };

  return (
    <MIAContext.Provider value={value}>
      {children}
    </MIAContext.Provider>
  );
};

export default MIAContext;
